import {
  Component,
  signal,
  OnInit,
  computed,
  inject,
  PLATFORM_ID,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { trigger, style, animate, transition } from '@angular/animations';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { rxResource } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { EventService, SettingService } from '@millionshow/api-client';
import { toSignal } from '@angular/core/rxjs-interop';
import HeaderComponent from '../header/header.component';
import FooterComponent from '../footer/footer/footer.component';
import { Icon } from '@millionshow/client-libs';
import { NAV_ITEMS } from '../../const/nav-items.const';
import FloatingButtonsComponent from '../../components/floating-buttons/floating-buttons.component';
import { PopupBannerComponent } from '../../components/popup-banner/popup-banner.component';
import { ScreenSizeStore } from '../../stores/screen-size.store';

@Component({
  selector: 'app-client-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    HeaderComponent,
    FooterComponent,
    Icon,
    FloatingButtonsComponent,
    PopupBannerComponent,
  ],
  templateUrl: './client-layout.component.html',
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ transform: 'translateX(100%)', opacity: 0 }),
        animate(
          '300ms ease-out',
          style({ transform: 'translateX(0)', opacity: 1 })
        ),
      ]),
      transition(':leave', [
        animate(
          '300ms ease-in',
          style({ transform: 'translateX(100%)', opacity: 0 })
        ),
      ]),
    ]),
    trigger('slideDown', [
      transition(':enter', [
        style({ transform: 'translateY(-100%)', opacity: 0 }),
        animate(
          '400ms ease-out',
          style({ transform: 'translateY(0)', opacity: 1 })
        ),
      ]),
      transition(':leave', [
        animate(
          '300ms ease-in',
          style({ transform: 'translateY(-100%)', opacity: 0 })
        ),
      ]),
    ]),
  ],
})
export default class ClientLayoutComponent implements OnInit {
  private readonly eventService = inject<EventService>(EventService);
  private readonly router = inject<Router>(Router);
  private readonly settingService = inject(SettingService);
  private readonly platformId = inject(PLATFORM_ID);

  private get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  logoData = toSignal(
    this.settingService.settingControllerFindByKey({
      key: 'startfair_logo_url',
    })
  );

  menuOpen = signal<boolean>(false);
  eventPopupVisible = signal<boolean>(false);
  isHomePage = signal<boolean>(false);
  navItems = NAV_ITEMS;

  relatedSites = [
    { label: '마이스좋은공간', url: 'https://xn--s39awr644d2fb.kr/' },
    { label: '밀리언쇼', url: 'https://millionshow.kr/' },
    { label: '건축박람회', url: 'https://www.allhouse.or.kr/' },
    { label: '명절선물박람회', url: 'http://www.giftfair.co.kr/' },
    { label: '농기자재전시회', url: 'https://www.agritex.kr/' },
  ];

  // 진행중인 행사 데이터 가져오기
  $events = rxResource({
    stream: () =>
      this.eventService.eventControllerFindAllV1({
        status: 'ONGOING',
        type: 'STARTFAIR' as const,
      }),
  });

  // 가장 가까운 행사 1개만 추출 (D-1 이상만, 당일/시작된 행사 제외)
  upcomingEvent = computed(() => {
    const items = this.$events.value() || [];
    if (items.length === 0) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // 시작일이 내일 이후인 행사만 필터링 (D-1 이상만)
    const upcomingItems = items.filter(event => {
      const startDate = new Date(event.startAt);
      startDate.setHours(0, 0, 0, 0);

      const diffTime = startDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      // diffDays >= 1 이면 D-1 이상 (내일 이후)
      return diffDays >= 1;
    });

    if (upcomingItems.length === 0) return null;

    // 시작일 기준으로 정렬하여 가장 빠른 행사 반환
    const sorted = [...upcomingItems].sort(
      (a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime()
    );

    return sorted[0];
  });

  // D-Day 계산
  dDay = computed(() => {
    const event = this.upcomingEvent();
    if (!event) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const startDate = new Date(event.startAt);
    startDate.setHours(0, 0, 0, 0);

    const diffTime = startDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'D-Day';
    if (diffDays > 0) return `D-${diffDays}`;
    return `D+${Math.abs(diffDays)}`;
  });

  private readonly screenSizeStore = inject(ScreenSizeStore);
  isMobileSize = this.screenSizeStore.isMobileSize;
  isFloatingHiddenPage = signal<boolean>(false);

  ngOnInit(): void {
    this.checkEventPopup();
    this.updatePageStatus();

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updatePageStatus();
      });
  }

  updatePageStatus(): void {
    const url = this.router.url;
    this.isHomePage.set(url === '/' || url === '');

    // 모바일에서 플로팅 버튼을 가릴 페이지 (약관, 이벤트 상세)
    const isPolicy = url.includes('/policy');
    const isEventDetail =
      url.startsWith('/event/') && url.split('/').length > 2;
    const isRegistration = url.includes('/registration-form');
    const isBoothApp = url.includes('/booth-application-form');
    this.isFloatingHiddenPage.set(
      isPolicy || isEventDetail || isRegistration || isBoothApp
    );
  }

  toggleMenu(): void {
    this.menuOpen.set(!this.menuOpen());
    if (this.isBrowser) {
      document.body.style.overflow = this.menuOpen() ? 'hidden' : '';
    }
  }

  checkEventPopup(): void {
    if (!this.isBrowser) {
      this.eventPopupVisible.set(false);
      return;
    }

    const POPUP_KEY = 'event_popup_hidden';
    const hiddenUntil = localStorage.getItem(POPUP_KEY);

    if (hiddenUntil) {
      const hiddenDate = new Date(hiddenUntil);
      const now = new Date();

      if (now < hiddenDate) {
        this.eventPopupVisible.set(false);
        return;
      } else {
        localStorage.removeItem(POPUP_KEY);
      }
    }

    this.eventPopupVisible.set(true);
  }

  closeEventPopup(): void {
    if (!this.isBrowser) {
      return;
    }

    const POPUP_KEY = 'event_popup_hidden';
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);

    localStorage.setItem(POPUP_KEY, tomorrow.toISOString());
    this.eventPopupVisible.set(false);
  }
}
